<template>
  <div class='donut-chart-section'>
    <svg :height="`${height}px`" :width="`${width}px`" :viewBox="`0 0 ${width} ${height}`">
      <path
        v-for='(value, index) in series' :key='index'
        fill="none"
        :stroke="colorsFormatted[index]"
        stroke-width="22"
        stroke-linecap="round"
        :d="dValuesFormatted[index]"
      />
      <circle
        id='circle'
        :fill="colorsFormatted[0]"
        :cx="circlePosition.cx"
        :cy="circlePosition.cy"
        r="11.1"
      />
      <text v-if='showTotal' :x='center.x' :y='center.y+13' class='total-value' :fill='labelTextColor'>{{sumAllValues}}</text>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'DonutChart',
  props: {
    radius: {
      type: Number,
      default: 75,
    },
    showTotal: {
      type: Boolean,
      default: false,
    },
    width: {
      type: Number,
      default: () => 250,
    },
    height: {
      type: Number,
      default: () => 250,
    },
    colors: {
      type: Array,
      default: () => ['#FD952D', '#00D0C4', '#DF399F', '#682FD2'],
    },
    series: {
      type: Array,
      default: () => [],
    },
  },
  updated() {
    this.init();
  },
  created() {
    this.init();
  },
  computed: {
    labelTextColor() {
      return this.sumAllValues === 0 ? 'var(--text-tertiary)' : 'var(--text-primary)';
    },
    circlePosition() {
      if (this.dValuesForSeries && this.dValuesForSeries.length > 1) {
        const pointValues = this.dValuesForSeries[0]?.split(' ').slice(-2);
        return { cx: parseFloat(pointValues[0]) + 0.4, cy: pointValues[1] };
      }
      // ignored
      return {cx: -100, cy: -100};
    },
    center() {
      return {
        x: this.width / 2,
        y: this.height / 2,
      };
    },
    sumAllValues() {
      return this.series.reduce((a, b) => a + b, 0);
    },
    dValuesForSeries() {
      const arr = [];
      let startAngle = 0;
      let endAngle = 0;
      let allUnitsCount = 0;

      for (let i = 0; i < this.series.length; i += 1) {
        const item = this.series[i];
        let currentUnits = (item / this.sumAllValues) * 100;

        allUnitsCount += currentUnits;

        if (i === this.series.length - 1 && allUnitsCount < 100) {
          currentUnits += (100 - allUnitsCount);
        }
        endAngle = startAngle + ((360 / 100) * currentUnits);

        const startingPoint = this.polarToCartesian(this.center.x, this.center.y, this.radius, endAngle);
        const endingPoint = this.polarToCartesian(this.center.x, this.center.y, this.radius, startAngle);
        const largeArcFlag = (endAngle - startAngle) <= 180 ? 0 : 1;

        const currentD = ['M', startingPoint.x, startingPoint.y, 'A', this.radius, this.radius, 0, largeArcFlag, 0, endingPoint.x, endingPoint.y].join(' ');
        arr.push(currentD);
        startAngle = endAngle;
      }
      return arr;
    },
    dValuesFormatted() {
      return this.dValuesForSeries.length > 1 ? this.dValuesForSeries : ['M 125 50 A 75 75 0 1 0 125.0001 50'];
    },
    colorsFormatted() {
      if (this.series.length === 0 || this.sumAllValues === 0) {
        return ['#A9B6C3'];
      }
      const currentColors = this.colors;
      const diff = this.series.length - this.colors.length;
      if (diff > 0) {
        for (let i = 0; i < diff; i += 1) {
          let randomColor = Math.floor(Math.random() * 16777215).toString(16);
          while (currentColors.includes(randomColor)) {
            randomColor = Math.floor(Math.random() * 16777215).toString(16);
          }
          currentColors.push(`#${randomColor}`);
        }
      } else if (diff < 0) {
        currentColors.slice(diff * -1);
      }
      return currentColors;
    },
  },
  methods: {
    init() {
      if (this.series.length === 0) {
        this.series.push(0);
      }
    },
    angleInRadians(angleInDegrees) {
      return ((angleInDegrees - 90) * Math.PI) / 180.0;
    },
    polarToCartesian(centerX, centerY, radius, angleInDegrees) {
      return {
        x: centerX + radius * Math.cos(this.angleInRadians(angleInDegrees)),
        y: centerY + radius * Math.sin(this.angleInRadians(angleInDegrees)),
      };
    },
  },
  watch: {
    series: {
      // eslint-disable-next-line no-unused-vars
      handler(newVal, oldVal) {
        this.init();
      },
      deep: true, // Enables deep watching of the array elements
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

#circle {
  stroke: none;
}

.total-value {
  font-family: $font-family-black;
  font-size: $font-size-5xl;
  font-weight: 900;
  letter-spacing: 0;
  text-anchor: middle;
  color: var(--text-secondary);
}
</style>
